import React from 'react';
import { css } from '@emotion/react';

interface ThumbnailProps {
  /** 썸네일 제목 */
  title: string;
  /** 배경 이미지 URL */
  image?: string;
  /** 선택 여부 */
  isSelected?: boolean;
  /** 클릭 핸들러 */
  onClick?: () => void;
  /** 추가적인 CSS 클래스 */
  className?: string;
}

function Thumbnail({
  title,
  image,
  isSelected = false,
  onClick,
  className
}: ThumbnailProps) {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.();
    }
  };

  return (
    <button
      css={[ThumbnailContainer, className]}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      type="button"
    >
      {/* 배경 이미지 */}
      {image && (
        <div
          css={css`
            position: absolute;
            inset: 0;
            background-image: ${isSelected
              ? `linear-gradient(0deg, rgba(120, 0, 255, 0.20) 0%, rgba(120, 0, 255, 0.20) 100%), url(${image})`
              : `url(${image})`
            };
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
          `}
        />
      )}

      {/* 선택된 상태일 때는 보라색 텍스트 오버레이, 아니면 기본 오버레이 */}
      {isSelected ? (
        <div css={SelectedTextOverlay} />
      ) : (
        <div css={DefaultOverlay} />
      )}

      {/* 하단 텍스트 라벨 */}
      <div css={LabelContainer}>
        <span css={LabelText}>{title}</span>
      </div>

      {/* 선택된 상태일 때만 표시되는 요소들 */}
      {isSelected && (
        <>
          {/* 체크 아이콘 */}
          <div css={CheckIcon}>
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              css={CheckMark}
            >
              <path
                d="M10 3L4.5 8.5L2 6"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {/* 선택된 상태 테두리 */}
          <div css={SelectedBorder} />
        </>
      )}
    </button>
  );
}

export default Thumbnail;

const ThumbnailContainer = css`
  position: relative;
  width: 114px;
  height: 114px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  text-align: inherit;
`;

const DefaultOverlay = css`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 32px; /* 8 * 4px = 32px */
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SelectedTextOverlay = css`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 32px; /* 8 * 4px = 32px */
  background: rgba(120, 0, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LabelContainer = css`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LabelText = css`
  font-family: 'Pretendard', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #ffffff;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CheckIcon = css`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #7800ff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CheckMark = css`
  width: 12px;
  height: 12px;
`;

const SelectedBorder = css`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 2px solid #7800ff;
  border-radius: 8px;
  pointer-events: none;
`;  